<template>
  <div v-if="planTerm && priceRange" class="protection-plan-container plan-sec">
    <div class="plan-content-wrapper grid-x nowrap margin-bottom-m">
      <Checkbox v-model="checkbox.value" name="choose-protection-plan" :options="checkbox.options" />
      <div class="plan-description">
        <div class="plan-name">
          Add
          <button v-if="isMcom" aria-haspopup="dialog" aria-label="Show Protection Plan" class="link-body" @click.stop="openProtectionPlanDrawer">
            {{ planTerm }} plan
          </button>
          <a v-else>{{ planTerm }} plan</a>

          <span v-if="isMultiPlan"> With Mattress Cover </span>
          <span v-if="!checkbox.value.length || !isMultiPlan" class="price"> - {{ planDescription?.priceRange }}</span>
          <span v-else class="price">- {{ selectedPrice }}</span>
        </div>
      </div>
    </div>
    <div class="plan-options-wrapper" :class="{ 'margin-bottom-m': radioOptions.length }">
      <Radio
        v-if="isMultiPlan && !!checkbox.value.length"
        v-model="protectionPlan"
        :options="radioOptions"
        data-plan-id="plan.planId"
        data-price="plan.price"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, h, watch, computed, markRaw, defineAsyncComponent } from 'vue';

import Checkbox from '@atomic-ui/checkboxBasic/CheckboxBasic';
import Radio from '@atomic-ui/radio/Radio';

import { useSlideout } from '~/composables/utils/useSlideout';
import { useBrand } from '~/composables/useBrand';

import { useSelectedProtectionPlan } from '~/modules/protection-plan/composables/useSelectedProtectionPlan';
import { useProtectionPlanBigTicket } from '~/modules/protection-plan/composables/useProtectionPlanBigTicket';
import { useProduct } from '~/modules/product/composables/useProduct';

const { showSlideout } = useSlideout();

// TODO: It's overcomplicated, refactor it
const { planDescription, allPlans = [], fireLinkTagOnPlanSelection } = useProtectionPlanBigTicket();
const { productId, productData } = useProduct();

const { planTerm, priceRange } = planDescription || {};

const isMultiPlan = allPlans.length > 1;

const { isMcom } = useBrand();

const initialPlanId = allPlans[0]?.planId;
const { protectionPlan } = useSelectedProtectionPlan(initialPlanId);

const selectedPrice = computed(() => {
  const selectedPlan = allPlans.find((plan) => plan.planId === protectionPlan.value);
  return selectedPlan ? selectedPlan.price : null;
});

/**
 * @param {{ planId: string }} plan - The selected plan
 * @returns {(event: Event) => void} - The event handler
 */
const slideoutHandler = (plan) => (event) => {
  showSlideout({
    id: 'protection-plan-drawer',
    title: 'Protection plan',
    props: {
      productId,
      product: productData,
      planId: plan.planId,
    },
    component: markRaw(
      defineAsyncComponent(
        /* istanbul ignore next */ () =>
          import(/* webpackChunkName: "ProtectionPlanBigTicketInfo" */ './slideoutContent/ProtectionPlanBigTicketInfo.vue'),
      ),
    ),
  });
  event.preventDefault();
};

// Transform the imported options to match the expected structure for the Radio component
const radioOptions = allPlans.map((plan) => ({
  value: plan.planId,
  label: h('span', [
    h(
      'a',
      {
        href: '#',
        onclick: slideoutHandler(plan),
      },
      plan.planName,
    ),
    ` - ${plan.price}`,
  ]),
  labelType: 'html', // Indicate that the label should be treated as HTML
}));

const checkbox = ref({
  value: [],
  options: [
    {
      id: 'protection-plan-cb',
      ariaLabel: `${planDescription?.planTerm} plan`,
      value: false,
    },
  ],
});

const openProtectionPlanDrawer = () => {
  showSlideout({
    id: 'protection-plan-drawer',
    title: 'Protection plan',
    // Ignore next line with istanbul because it's not possible to test the dynamic import
    component: markRaw(
      defineAsyncComponent(
        /* istanbul ignore next */ () =>
          import(/* webpackChunkName: "ProtectionPlanBigTicketInfo" */ './slideoutContent/ProtectionPlanBigTicketInfo.vue'),
      ),
    ),
  });
};

if (isMcom) {
  watch([checkbox.value.value], ([newValue]) => {
    const isChecked = !!newValue.length;
    protectionPlan.value = isChecked ? initialPlanId : '';
    fireLinkTagOnPlanSelection(isChecked);
  });
}
</script>
